import React, {
	useEffect,
	useMemo,
	useState,
	MouseEvent,
} from 'react'

import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { LoanList } from '@matech/thebigpos-sdk'
import { TableColumn } from 'react-data-table-component'

import DataTable, {
	ActionItem,
} from '../../../../components/DataTable'
import { useAlert } from '../../../../hooks'
import { getTheme } from '../../../../config'
import useUser from '../../../../hooks/useUser'
import useLoanSearch from '../hooks/useLoanSearch'
import useSearch from '../../../../hooks/useSearch'
import BorrowerNamesCell from './BorrowerNamesCell'
import useDebounce from '../../../../hooks/useDebounce'
import PipelineExpandedRow from './PipelineExpandedRow'
import { UserAppContextState } from '../../../../types'
import BorrowerAddressCell from './BorrowerAddressCell'
import { formatDate } from '../../../../services/helper'
import formatLoanAmount from '../utils/formatLoanAmount'
import usePagination from '../../../../hooks/usePagination'
import formatRoleSelector from '../utils/formatRoleSelector'
import formatBorrowerName from '../utils/formatBorrowerName'
import type { LoanSearchCriteria } from '../hooks/useLoanSearch'
import { useAppContext } from '../../../../components/AppContext'
import { useLoanContext } from '../../../../contexts/LoanContext'
import { navigationLinking } from '../../../../services/navigation'
import { usePipelineShareContext } from '../providers/PipelineProvider'
import { DEFAULT_SORT_DIRECTION } from '../../../../constants/pagination'

type LoanTabProps = {
	pageTitle: string
}

const LOAN_PIPELINE_TABLE_DEFAULT_SORT_BY = 'startDate'

const LoanPipelineTable = ({ pageTitle }: LoanTabProps) => {
	const theme = getTheme()
	const { dataTable: breakpoint } = theme.breakpoints
	const { formatMessage } = useIntl()

	const { alert } = useAlert()
	const navigate = useNavigate()
	const debounce = useDebounce<string>()
	const { isBorrower, isRealtor, isLoanOfficer, isLoanAdmin } =
		useUser()

	const { state } = useAppContext()
	const { setActiveLoanId } = useLoanContext()
	const { searchText, handleClearSearchClick, handleSearchChange } =
		useSearch()

	const { handleOpenShareLoanModal } = usePipelineShareContext()

	const {
		sortBy,
		pageSize,
		pageNumber,
		sortDirection,
		handleSort,
		handlePageChange,
		handleRowsPerPageChange,
	} = usePagination<LoanList>({
		defaultSortBy: LOAN_PIPELINE_TABLE_DEFAULT_SORT_BY,
		defaultSortDirection: DEFAULT_SORT_DIRECTION,
	})

	// ToDo: Remove this casting when the AppContext turns in Typescript
	const { user } = state as unknown as { user: UserAppContextState }

	const [criteria, setCriteria] = useState<LoanSearchCriteria>({
		searchText: '',
	})

	const { isError, isProgressPending, refetch, data, totalRows } =
		useLoanSearch({
			// ToDo: Refactor src/components/AppContext/index.js to Typescript to remove this casting
			userId: user?.id,
			criteria,
			query: {
				pageNumber,
				pageSize,
				sortBy,
				sortDirection,
			},
		})

	// ToDo: Will be uncomment when the definition from screen is done
	// const handleStatusChange = (e: SelectChangeEvent<unknown>) =>
	// 	setCriteria((prev) => ({
	// 		...prev,
	// 		status: e.target.value as string,
	// 	}))

	const handleSearchTextChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		handleSearchChange(e)

		debounce(e.target.value, (debouncedValue) => {
			setCriteria((prev) => ({
				...prev,
				searchText: debouncedValue,
			}))
		})
	}

	const handleClearSearchTextChange = () => {
		handleClearSearchClick()
		setCriteria((prev) => ({
			...prev,
			searchText: '',
		}))
	}

	const columns = useMemo(() => {
		return [
			{
				name: formatMessage({ id: 'pipeline.columns.loan_number' }),
				hide: breakpoint,
				omit: isBorrower,
				selector: (row) => row.loanNumber || '',
				sortable: true,
				sortField: 'loanNumber',
			},
			{
				name: formatMessage({ id: 'pipeline.columns.loan_amount' }),
				selector: (row) => row?.totalLoanAmount || 0,
				cell: (row) => formatLoanAmount(row.totalLoanAmount),
				sortable: true,
				sortField: 'totalLoanAmount',
			},
			{
				name: formatMessage({ id: 'global.name' }),
				selector: (row) => formatBorrowerName(row.userLoans),
				cell: (row) => (
					<BorrowerNamesCell userLoans={row.userLoans} />
				),
			},
			{
				name: formatMessage({ id: 'users.loan_officer' }),
				hide: breakpoint,
				omit: isLoanOfficer,
				selector: (row) => row.loanOfficer?.name || '',
				cell: (row) =>
					row.loanOfficer?.name ||
					formatMessage({ id: 'pipeline.not_yet_assigned' }),
			},
			{
				name: formatMessage({ id: 'global.role' }),
				hide: breakpoint,
				omit: !isRealtor,
				selector: (row) =>
					formatRoleSelector(
						user.email,
						row.sellerAgentContact?.email,
						row.buyerAgentContact?.email
					),
			},
			{
				name: formatMessage({ id: 'pipeline.columns.address' }),
				hide: breakpoint,
				selector: (row) => row.propertyAddress?.street || '',
				cell: (row) => (
					<BorrowerAddressCell
						loanId={row.loanID}
						address={row.propertyAddress}
					/>
				),
			},
			{
				name: formatMessage({
					id: 'pipeline.columns.application_date',
				}),
				hide: breakpoint,
				selector: (row) => row.startDate || '',
				cell: (row) => formatDate(row.startDate, false),
				sortable: true,
			},
			{
				name: formatMessage({ id: 'global.status' }),
				hide: breakpoint,
				omit: isBorrower,
				selector: (row) => row.status || '',
				sortable: false,
			},
		] as TableColumn<LoanList>[]
	}, [
		formatMessage,
		user.email,
		breakpoint,
		isBorrower,
		isLoanOfficer,
		isRealtor,
	])

	const onRowClicked = (row: LoanList) => {
		if (row.loanID) {
			// ToDo: Not sure how row would be null but getting logs in sentry
			setActiveLoanId(row.loanID)
		}

		navigate(`/${navigationLinking.Portal}`)
	}

	const actionItems: ActionItem<LoanList>[] = [
		{
			name: isLoanAdmin
				? formatMessage({ id: 'pipeline.impersonate' })
				: formatMessage({ id: 'pipeline.view_application' }),
			onClick: (e: MouseEvent, row: LoanList) => onRowClicked(row),
		},
		{
			name: formatMessage({ id: 'global.share' }),
			onClick: (_e: MouseEvent, row: LoanList) =>
				handleOpenShareLoanModal?.({
					loanId: row.loanID,
					loanType: 'complete',
				}),
		},
	]

	useEffect(() => {
		if (!isError) return

		alert(formatMessage({ id: 'alerts.pipeline_loading_error' }), {
			severity: 'error',
		})
	}, [isError, alert, formatMessage])

	return (
		<DataTable<LoanList>
			data={data}
			columns={columns}
			defaultSortAsc={false}
			title={pageTitle}
			defaultSortFieldId={LOAN_PIPELINE_TABLE_DEFAULT_SORT_BY}
			pagination
			paginationServer
			paginationTotalRows={totalRows}
			progressPending={isProgressPending}
			onRefreshClick={refetch}
			fixedHeader
			actionItems={actionItems}
			expandableRowsComponent={PipelineExpandedRow}
			sortServer
			onSort={handleSort}
			onChangePage={handlePageChange}
			onChangeRowsPerPage={handleRowsPerPageChange}
			searchText={searchText}
			onSearchChange={handleSearchTextChange}
			onClearSearchClick={handleClearSearchTextChange}
			onRowClicked={onRowClicked}
			// ToDo: This filter will be implemented later in #9687051564
			// Task link: https://mortgage-automation-technologies.monday.com/boards/5842126081/pulses/9687051564
			// searchFilters={
			// 	<LoanPipelineFilters
			// 		hasPermission={!isBorrower}
			// 		statusValue={criteria.status}
			// 		onChangeStatusValue={handleStatusChange}
			// 	/>
			// }
		/>
	)
}

export default LoanPipelineTable
